import type { ReactNode } from "react";
import { Col, Row } from "react-bootstrap";
import DetailedMetric from "./DetailedMetric";
import TrellisLink from "../basic/TrellisLink";
import type { MetricConfig } from "../../../modules/merchandising/utils/metrics";
import SparklineChart from "./SparklineChart";

interface TitleProps {
  icon?: string;
  title: string;
  byline?: ReactNode;
}

const Title = ({ icon, title, byline }: TitleProps) => (
  <div style={{ fontSize: "20px" }} className="d-flex align-items-start">
    {icon && (
      <div
        style={{
          paddingRight: "10px",
          display: "inline-block",
          backgroundImage: `url("${icon}")`,
          width: "60px",
          height: "50px",
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      />
    )}
    <div style={{ width: `calc(100%  ${icon ? "- 60px" : ""})` }}>
      <div className="text-truncate">{title}</div>
      <div style={{ color: "#666", fontSize: "12px" }}>{byline}</div>
    </div>
  </div>
);

interface DetailedMetricRowProps<T> {
  title: string;
  byline?: ReactNode;
  icon?: string;
  /** Totals data object for metric display */
  totals?: { data: T | undefined; loading?: boolean };
  /** Timeline data array for sparkline visualization */
  timeline?: { data: (T & { start_date: string })[] | undefined; loading?: boolean };
  metrics?: MetricConfig[];
  to?: string;
}

const DetailedMetricRow = <T,>({ title, byline, icon, totals, timeline, metrics, to }: DetailedMetricRowProps<T>) => {
  if (!metrics) return null;

  const firstMetric = metrics[0];

  return (
    <Col xs={12} className="py-4" lg={12}>
      <Row>
        <Col xs={12} className="" lg={3}>
          {to ?
            <TrellisLink to={to} className="text-decoration-none">
              <Title icon={icon} title={title} byline={byline} />
            </TrellisLink>
          : <Title icon={icon} title={title} byline={byline} />}
        </Col>
        <Col xs={12} className="mb-md-0 mb-4" md={4} lg={2}>
          {firstMetric && timeline?.data && timeline.data.length > 0 && (
            <SparklineChart
              data={timeline.data.map((d) => firstMetric.accessor(d) ?? 0)}
              height={75}
              color={`${firstMetric.color}a0`}
            />
          )}
        </Col>
        <Col xs={12} md={8} lg={7}>
          <Row>
            {metrics.map((m, i) => (
              <Col xs={6} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`} lg={3}>
                <div className="">
                  <div>
                    <DetailedMetric
                      metric={m}
                      data={totals?.data}
                      showTarget={!!m.prev_accessor}
                      isLoading={totals?.loading}
                    />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </Col>
  );
};

export default DetailedMetricRow;
